import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/AISubscription/LoadingSpinner';
import { toast } from 'react-toastify';
import { BASE_URL, API_BASE_URL, institute_id } from '../config/config';

/**
 * AI Doubt Solver Page
 * Allows students to ask doubts and get AI-powered answers
 */
const AIDoubtSolver = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkFeatureAccess, logFeatureUsage } = useSubscription();
  
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkAccess();
  }, []);

  const checkAccess = async () => {
    const access = await checkFeatureAccess('doubt_solver');
    if (!access.has_access) {
      toast.error(access.message || 'You need to upgrade to use this feature');
      navigate('/subscription/plans');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Extract base64 data without the data:image/xxx;base64, prefix
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!question.trim() && !selectedImage) {
      toast.error('Please enter your question or upload an image');
      return;
    }

    setLoading(true);
    
    try {
      const requestBody = {
        question: question || 'Please explain what is shown in the image',
        subject: '',
        context: '',
        user_id: user?.id
      };

      // If image is selected, convert to base64 and add to request
      if (selectedImage) {
        const base64Image = await convertImageToBase64(selectedImage);
        requestBody.image = base64Image;
        requestBody.image_type = selectedImage.type;
      }

      // Call the AI API
      const response = await fetch(`${API_BASE_URL}/ai-subscription/features/doubt-solver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Institute-ID': user?.institute_id || '',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        const aiAnswer = data.data.answer;
        setAnswer(aiAnswer);
        
        // Add to history
        const newEntry = {
          id: Date.now(),
          question: question || 'Image question',
          answer: aiAnswer,
          image: imagePreview,
          timestamp: new Date().toLocaleString()
        };
        setHistory([newEntry, ...history]);
        
        // Log usage
        await logFeatureUsage('doubt_solver', {
          question: question.substring(0, 100),
          has_image: !!selectedImage
        });
        
        toast.success('Answer generated!');
        
        // Clear form
        setQuestion('');
        removeImage();
      } else {
        toast.error(data.message || 'Failed to get answer. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to get answer. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setHistory([]);
    setAnswer('');
    setQuestion('');
    toast.success('History cleared');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <PublicHeader />
      
      <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 md:py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/ai/features')}
            className="text-purple-600 hover:text-purple-700 mb-4 flex items-center gap-2"
          >
            ← Back to AI Features
          </button>
          
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
            ❓ AI Doubt Solver
          </h1>
          <p className="text-gray-600">
            Get instant, detailed answers to your questions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Question Input */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
                Ask Your Question
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Question
                  </label>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Type your question here... Or upload an image below."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[120px] resize-y"
                    disabled={loading}
                  />
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📷 Upload Image (Optional)
                  </label>
                  
                  {!imagePreview ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                        disabled={loading}
                      />
                      <label
                        htmlFor="image-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <div className="text-4xl">📸</div>
                        <p className="text-sm text-gray-600">
                          Click to upload an image of your question
                        </p>
                        <p className="text-xs text-gray-500">
                          Perfect for math problems, diagrams, or handwritten notes
                        </p>
                        <p className="text-xs text-gray-400">
                          PNG, JPG, JPEG (Max 5MB)
                        </p>
                      </label>
                    </div>
                  ) : (
                    <div className="relative border-2 border-purple-300 rounded-lg p-3 bg-purple-50">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-h-64 mx-auto rounded-lg shadow-md"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition-colors"
                        disabled={loading}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || (!question.trim() && !selectedImage)}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      Getting Answer...
                    </>
                  ) : (
                    <>
                      🤖 Get AI Answer
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Answer Display */}
            {answer && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-md p-4 sm:p-6 border-2 border-purple-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  💡 Answer
                </h3>
                
                {/* Show uploaded image if present */}
                {imagePreview && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Your Question:</p>
                    <img
                      src={imagePreview}
                      alt="Question"
                      className="max-h-48 rounded-lg shadow-md border-2 border-purple-200"
                    />
                  </div>
                )}
                
                <div className="prose prose-sm sm:prose max-w-none">
                  <div 
                    className="text-gray-700 whitespace-pre-wrap leading-relaxed"
                    style={{
                      wordBreak: 'break-word'
                    }}
                  >
                    {answer.split('\n').map((paragraph, idx) => (
                      paragraph.trim() ? (
                        <p key={idx} className="mb-3">{paragraph}</p>
                      ) : null
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - History */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  📚 History
                </h3>
                {history.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Clear
                  </button>
                )}
              </div>

              {history.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">
                  Your question history will appear here
                </p>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => {
                        setQuestion(item.question);
                        setAnswer(item.answer);
                        if (item.image) {
                          setImagePreview(item.image);
                        }
                      }}
                    >
                      {item.image && (
                        <img
                          src={item.image}
                          alt="Question thumbnail"
                          className="w-full h-20 object-cover rounded mb-2"
                        />
                      )}
                      <p className="text-sm font-medium text-gray-800 line-clamp-2 mb-1">
                        {item.question}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.timestamp}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-6">
          <h3 className="text-lg font-bold text-blue-900 mb-3">
            💡 Tips for Better Answers
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span>✓</span>
              <span>Be specific and provide context for your question</span>
            </li>
            <li className="flex items-start gap-2">
              <span>✓</span>
              <span>Upload images of math problems, diagrams, or handwritten questions for better accuracy</span>
            </li>
            <li className="flex items-start gap-2">
              <span>✓</span>
              <span>Include relevant course topics or subjects</span>
            </li>
            <li className="flex items-start gap-2">
              <span>✓</span>
              <span>For images, ensure they are clear and well-lit</span>
            </li>
            <li className="flex items-start gap-2">
              <span>✓</span>
              <span>Break down complex questions into simpler parts</span>
            </li>
            <li className="flex items-start gap-2">
              <span>✓</span>
              <span>Ask follow-up questions for deeper understanding</span>
            </li>
          </ul>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIDoubtSolver;
